import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { MouseEvent } from 'react';

import subscribeLib from '@skyscanner-internal/save-to-list-client/store/subscriptions/subscribe';
import throttle from 'lodash/throttle';

import {
  BpkSaveButton,
  STYLE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-card-button';

import { withI18n } from '../../contexts/i18n';
import { MeaningfulBehaviourDataWrapper } from '../../contexts/meaningful-behaviour-data';
import { withMiniEventTracker } from '../../contexts/mini-events';
import SaveHotelsFloatingNotification from '../SaveHotelsFloatingNotification';

import SaveLimitErrorDialog from './SaveLimitErrorDialog';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { OnSavedStatusChanged } from '@shared/types/frontend/save-to-list';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Stay } from '@shared/types/utils';

import { SAVED_STATUS } from '@shared/constants/save-status';
import {
  isSelectedHotel,
  isSavedHotel,
  pushLoginWhileSave,
  removeHotel,
  saveHotel as saveHotelInStore,
  generateActionId,
  getActionIdBeforeLogging,
} from '@shared/data-access-frontend/save-to-list-store';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import { buildHotelSaveAttributes } from '@shared/utils-frontend/meaningful-behaviour-data/hotel-save';
import {
  ACTION_TYPE,
  SAVE_STATUS_TYPE,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoSaveStatusChanged } from '@shared/utils-frontend/mini-events/hotels-action';

type Props = {
  id: string;
  i18n: I18nService;
  stay: Stay;
  price?: number;
  userContext: UserContext;
  saveButtonStyle?: keyof typeof STYLE_TYPES;
  needSplitNotification?: boolean;
  changeSaveStatus?: OnSavedStatusChanged;
  clickArea: string;
  miniEventTracker: MiniEventTracker;
  correlationId?: string;
  destinationEntityId?: string;
};

const THROTTLE_TIME = 2000;

const SaveButton = ({
  changeSaveStatus = () => {},
  clickArea,
  correlationId,
  destinationEntityId,
  i18n,
  id,
  miniEventTracker: { trackHotelsAction },
  needSplitNotification = false,
  price = 0,
  saveButtonStyle = STYLE_TYPES.default,
  stay,
  userContext: {
    userPreferences: { isLoggedIn },
  },
}: Props) => {
  const [saveStatus, setSaveStatus] = useState(SAVED_STATUS.RESET);
  const [statusText, setStatusText] = useState<string>();
  const [headingText, setHeadingText] = useState<string>();
  const [subHeadingText, setSubHeadingText] = useState<string>();

  const handleStatusChanged: OnSavedStatusChanged = ({
    payload: {
      headingText: newHeadingText,
      statusText: newStatusText,
      subHeadingText: newSubHeadingText,
    },
    saveStatus: newSaveStatus,
  }) => {
    setSaveStatus(newSaveStatus);
    setStatusText(newStatusText);
    setHeadingText(newHeadingText);
    setSubHeadingText(newSubHeadingText);
  };
  const onSavedStatusChanged: OnSavedStatusChanged = needSplitNotification
    ? changeSaveStatus
    : handleStatusChanged;
  const isSaved = useSyncExternalStore(
    subscribeLib,
    () =>
      isSavedHotel({
        i18n,
        id,
        stay,
        destinationEntityId,
      }),
    () => false,
  );

  useEffect(() => {
    // Get the `hotelId` of the global variable `selectedHotelIdBeforeLogging` and continue to save the hotel after logging in.
    const saveHotelAfterLogin = async () => {
      if (!isSaved && isSelectedHotel(id) && isLoggedIn && price) {
        await saveHotel(getActionIdBeforeLogging());
      }
    };

    saveHotelAfterLogin();
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveHotel = async (actionId: string) => {
    const savedStatusText = await saveHotelInStore({
      i18n,
      stay,
      id,
      price,
      onSavedStatusChanged,
      actionId,
      destinationEntityId,
    });

    if (savedStatusText) {
      onSavedStatusChanged({
        payload: {
          statusText: savedStatusText,
        },
        saveStatus: SAVED_STATUS.SAVED,
      });
      trackHotelsAction(
        ACTION_TYPE.SAVE_STATUS_CHANGED,
        buildAdditionalInfoSaveStatusChanged({
          clickArea,
          hotelId: id,
          correlationId,
          isLoggedIn,
          stay,
          saveStatus: SAVE_STATUS_TYPE.SAVE_HOTEL_SUCCESS,
          actionId,
        }),
      );
    }
  };

  const removeHotelForSave = async (actionId: string) => {
    const removedStatusText = await removeHotel({
      i18n,
      stay,
      id,
      onSavedStatusChanged,
      actionId,
      destinationEntityId,
    });
    if (removedStatusText) {
      onSavedStatusChanged({
        payload: {
          statusText: removedStatusText,
        },
        saveStatus: SAVED_STATUS.REMOVED,
      });
      trackHotelsAction(
        ACTION_TYPE.SAVE_STATUS_CHANGED,
        buildAdditionalInfoSaveStatusChanged({
          clickArea,
          hotelId: id,
          correlationId,
          isLoggedIn,
          stay,
          actionId,
          saveStatus: SAVE_STATUS_TYPE.UNSAVED_HOTEL_SUCCESS,
        }),
      );
    }
  };

  const applyCheckedChange = async (actionId: string) => {
    if (!isLoggedIn) {
      await pushLoginWhileSave({
        i18n,
        id,
        actionId,
      });
    } else if (!isSaved) {
      await saveHotel(actionId);
    } else {
      await removeHotelForSave(actionId);
    }
  };
  const throttledApplyCheckedChange = useMemo(
    () => throttle(applyCheckedChange, THROTTLE_TIME),
    [isSaved, isLoggedIn, price, stay], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onCheckedChange = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const actionId = generateActionId();
    trackHotelsAction(
      ACTION_TYPE.SAVE_STATUS_CHANGED,
      buildAdditionalInfoSaveStatusChanged({
        clickArea,
        hotelId: id,
        correlationId,
        actionId,
        isLoggedIn,
        stay,
        saveStatus: SAVE_STATUS_TYPE.SAVE_ICON_CLICKED,
      }),
    );

    throttledApplyCheckedChange(actionId);
  };

  const ariaLabel = i18n.translate(
    isSaved ? 'HOTEL_Remove_save' : 'HOTEL_Save_Item',
  );

  if (!price) {
    return null;
  }

  return (
    <>
      <MeaningfulBehaviourDataWrapper
        {...buildHotelSaveAttributes({ hotelId: id, saveStatus, isCta: true })}
      >
        <BpkSaveButton
          checked={isSaved}
          accessibilityLabel={ariaLabel}
          style={saveButtonStyle}
          onCheckedChange={onCheckedChange}
        />
      </MeaningfulBehaviourDataWrapper>
      <SaveLimitErrorDialog
        headingText={headingText}
        subHeadingText={subHeadingText}
        isOpen={!!headingText}
        onSavedStatusChanged={onSavedStatusChanged}
      />
      {!needSplitNotification && (
        <SaveHotelsFloatingNotification
          onSavedStatusChanged={onSavedStatusChanged}
          saveStatus={saveStatus}
          statusText={statusText}
          hotelId={id}
          clickArea={clickArea}
        />
      )}
    </>
  );
};

export default withMiniEventTracker(withUserContext(withI18n(SaveButton)));
